import React, { useEffect, useMemo, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { useVendoData } from '@/pages/Admin/Vendo';
import { PageHeader, PageTitle, Wrapper } from '@/pages/Admin/Vendo';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import { usePendingOutgoingDeviceIds } from '@/components/Admin/Vendo/PendingTransfers';
import { useUIStore } from '@/store/useUIStore';
import {
   listVlanZonesApi,
   updateVlanZoneApi,
   deleteVlanZoneApi,
   retryVlanZonesApi,
   type VlanZone,
   type OpenwrtWiring,
} from '@/api/vendo';
import OpenwrtTrunkCard from '@/components/Admin/Vendo/OpenwrtTrunkCard';
import MikrotikWiring, { mikrotikWiringFor } from '@/components/Admin/Prompt/MikrotikWiring';
import MikrotikPortRolesCard from '@/components/Admin/Vendo/MikrotikPortRolesCard';
import { canHostVlans } from '@/utils/vlanHosts';

// Push-lifecycle labels for the status dot (green/gray/red).
const STATUS_LABEL: Record<VlanZone['status'], string> = {
   live: 'Live',
   pending: 'Pending',
   failed: 'Failed',
};
const STATUS_HINT: Record<VlanZone['status'], string> = {
   live: 'Confirmed on the router.',
   pending: 'Saved — pushing to the router. This turns green once confirmed.',
   failed: "The router wouldn't accept this zone after several tries (it may be full). Remove some zones and try again.",
};
// An OpenWRT (OrangePi) main is not pushed to — it reads the zones on its next
// heartbeat and reports back what it built. Same dot, honest wording.
// Re-exported so existing imports keep working; the rule itself lives in one place.
export { canHostVlans } from '@/utils/vlanHosts';

const PENDING_HINT_OPENWRT =
   'Saved — the router applies it on its next check-in (about a minute). This turns green once it confirms.';

/**
 * /wifi-networks — VLAN-zone management (VLAN redesign 2026-05-31).
 *
 * Per the redesign, a MAIN vendo carries reusable VLAN "zones"
 * (router.vlans[]); each sub-vendo attaches to one zone (1 VLAN = 1 sub) in
 * the Add Sub-vendo wizard. This page is where the operator pre-creates and
 * manages those zones — single or in bulk — independent of any sub. All
 * zones are tagged on every router port; the downstream OLT/switch/AP
 * untags. Per-VLAN IPs (.1 gateway / .250 NodeMCU) are derived server-side.
 */
const WifiNetworks = () => {
   useVendoData({ sessions: false });
   const devices = useVendoDevicesStore((s) => s.devices);
   const pendingOutgoingIds = usePendingOutgoingDeviceIds();
   // Only main vendos the operator currently OWNS, fully set up, not mid-transfer:
   //   parent === null        → main (not a sub)
   //   isCurrentOwner         → not already sold
   //   setupStatus === 'done' → not still in the setup wizard
   //   !pendingOutgoing       → no transfer in progress
   //   canHostVlans           → MikroTik, or an OrangePi. Ray, 2026-09-13: "on
   //                            the networks page, on the device selection it
   //                            should only show the mikrotik and orangepi".
   //                            A Ruijie or Comfast main cannot host VLAN
   //                            sub-vendos, so it does not belong in the list.
   const mains = useMemo(
      () =>
         devices.filter(
            (d) =>
               d.parent === null &&
               d.isCurrentOwner !== false &&
               d.setupStatus === 'done' &&
               !pendingOutgoingIds.has(d._id) &&
               canHostVlans(d.router?.platform, d.router?.hwBoard)
         ),
      [devices, pendingOutgoingIds]
   );

   const handleShow = useUIStore((s) => s.handleShow);
   const [selectedId, setSelectedId] = useState<string>('');
   const [zones, setZones] = useState<VlanZone[]>([]);
   const [platform, setPlatform] = useState<'mikrotik' | 'openwrt'>('mikrotik');
   // Wiring choices for an OpenWRT main (which link carries the tags). Absent
   // on MikroTik, where every zone is tagged on every port.
   const [openwrt, setOpenwrt] = useState<OpenwrtWiring | null>(null);
   const [summary, setSummary] = useState<{
      total: number;
      live: number;
      pending: number;
      failed: number;
   }>({ total: 0, live: 0, pending: 0, failed: 0 });
   const [loading, setLoading] = useState(false);
   const [pushing, setPushing] = useState(false);
   const [zoneFilter, setZoneFilter] = useState<'all' | 'available' | 'used'>('all');
   const [page, setPage] = useState(0);
   const PAGE_SIZE = 12;

   // Pick the first main once the list arrives, and re-pick if the current
   // selection drops out of the list (e.g. it entered a transfer).
   useEffect(() => {
      if (!mains.length) return;
      if (!mains.some((m) => m._id === selectedId)) setSelectedId(mains[0]._id);
   }, [mains, selectedId]);

   const loadZones = async (id: string) => {
      if (!id) return;
      setLoading(true);
      try {
         const res = await listVlanZonesApi(id);
         setZones(res.zones);
         setPlatform(res.platform);
         setOpenwrt(res.openwrt ?? null);
         setSummary(res.summary);
         setPushing(!!res.pushing);
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Failed to load VLAN zones');
         setZones([]);
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      if (selectedId) loadZones(selectedId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedId]);

   // While a zone is still pending OR a send is in progress, the router is being
   // pushed in the background (immediate try + 30s cron). Poll so gray dots flip
   // to green/red — and so the "pushing" flag clears — on their own without the
   // operator refreshing.
   const hasPending = zones.some((z) => z.status === 'pending');
   useEffect(() => {
      if (!selectedId || (!hasPending && !pushing)) return;
      const t = setInterval(() => loadZones(selectedId), 5000);
      return () => clearInterval(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedId, hasPending, pushing]);

   // Heal stuck (red) zones: reset them and let the router start pushing again.
   const [retrying, setRetrying] = useState(false);
   const retryFailed = async () => {
      if (!selectedId) return;
      setRetrying(true);
      try {
         const res = await retryVlanZonesApi(selectedId);
         toast.success(res.msg);
         await loadZones(selectedId);
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Failed to retry zones');
      } finally {
         setRetrying(false);
      }
   };

   const selectedMain = mains.find((d) => d._id === selectedId);

   const availCount = useMemo(() => zones.filter((z) => !z.attachedSub).length, [zones]);
   const usedCount = zones.length - availCount;
   const filteredZones = useMemo(
      () =>
         zones.filter((z) =>
            zoneFilter === 'available'
               ? !z.attachedSub
               : zoneFilter === 'used'
               ? !!z.attachedSub
               : true
         ),
      [zones, zoneFilter]
   );
   const pageCount = Math.max(1, Math.ceil(filteredZones.length / PAGE_SIZE));
   const safePage = Math.min(page, pageCount - 1);
   const pageZones = filteredZones.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

   // Reset to page 1 whenever the vendo or filter changes.
   useEffect(() => {
      setPage(0);
   }, [selectedId, zoneFilter]);

   return (
      <Wrapper>
         <PageHeader>
            <PageTitle>Networks</PageTitle>
         </PageHeader>
         <Subtitle>
            VLAN zones for a main vendo. Create them here, then attach a
            sub-vendo to a zone when you add one. Each zone is one isolated
            network on its own subnet — one zone per sub-vendo.
         </Subtitle>

         {mains.length === 0 ? (
            <EmptyCard>
               No main vendos yet. Register a main vendo first, then come back
               to set up its VLAN zones.
            </EmptyCard>
         ) : (
            <>
               <SelectRow>
                  <FieldLabel htmlFor="main-select">Main vendo</FieldLabel>
                  <Select
                     id="main-select"
                     value={selectedId}
                     onChange={(e) => setSelectedId(e.target.value)}
                  >
                     {mains.map((m) => (
                        <option key={m._id} value={m._id}>
                           {m.name}
                        </option>
                     ))}
                  </Select>
                  {platform === 'openwrt' && <PlatformPill>OpenWRT</PlatformPill>}
                  {zones.length > 0 && (
                     <ProgressPill
                        $allLive={summary.failed === 0 && summary.pending === 0}
                        title="VLAN zones confirmed live on the router"
                     >
                        {summary.live}/{summary.total} live
                        {summary.pending > 0 && <Dim> · {summary.pending} pending</Dim>}
                        {summary.failed > 0 && <Fail> · {summary.failed} failed</Fail>}
                     </ProgressPill>
                  )}
                  <Spacer />
                  {summary.failed > 0 && (
                     <RetryBtn disabled={retrying} onClick={retryFailed}>
                        {retrying ? 'Retrying…' : `Try again (${summary.failed} failed)`}
                     </RetryBtn>
                  )}
                  <AddBtn
                     disabled={!selectedId || pushing}
                     title={
                        pushing
                           ? 'A send to the router is in progress — adding resumes in a moment'
                           : undefined
                     }
                     onClick={() =>
                        handleShow({
                           template: 'create-vlan-zone',
                           title: 'Add VLAN zone(s)',
                           data: {
                              deviceId: selectedId,
                              platform,
                              existingIds: zones.map((z) => z.vlanId),
                              onCreated: () => loadZones(selectedId),
                           },
                        })
                     }
                  >
                     + Add VLAN zone(s)
                  </AddBtn>
               </SelectRow>
               {pushing && (
                  <PushingNote>
                     Sending to the router… adding is paused for a moment and
                     resumes on its own.
                  </PushingNote>
               )}

               {platform === 'openwrt' && openwrt && (
                  <OpenwrtTrunkCard
                     deviceId={selectedId}
                     wiring={openwrt}
                     onSaved={(next) => setOpenwrt(next)}
                  />
               )}
               {/* MikroTik: the wiring picture, from the ports saved on this router
                   (WAN, owner port, isolation, per-port roles). Ray, 2026-10-06:
                   "it should also [be] in the networks". */}
               {platform === 'mikrotik' &&
                  (() => {
                     const w = mikrotikWiringFor(selectedMain?.router);
                     return w ? <MikrotikWiring ports={w.ports} isolated={w.isolated} title={w.title} /> : null;
                  })()}
               {/* Which port is Customer and which is Trunk, and the operator can
                   change it — every MikroTik model (Ray 2026-10-21: "how would a
                   CCR owner know which port is the trunk?"). */}
               {platform === 'mikrotik' && selectedMain && (
                  <MikrotikPortRolesCard
                     deviceId={selectedMain._id}
                     router={selectedMain.router as never}
                     onSaved={() => void useVendoDevicesStore.getState().fetch()}
                  />
               )}

               {zones.length > 0 && (
                  <FilterRow>
                     <FilterChip $active={zoneFilter === 'all'} onClick={() => setZoneFilter('all')}>
                        All ({zones.length})
                     </FilterChip>
                     <FilterChip
                        $active={zoneFilter === 'available'}
                        onClick={() => setZoneFilter('available')}
                     >
                        Available ({availCount})
                     </FilterChip>
                     <FilterChip $active={zoneFilter === 'used'} onClick={() => setZoneFilter('used')}>
                        Used ({usedCount})
                     </FilterChip>
                  </FilterRow>
               )}

               <ZoneTableCard>
                  {loading ? (
                     <SkeletonLines lines={3} />
                  ) : zones.length === 0 ? (
                     <Muted>
                        No VLAN zones on {selectedMain?.name ?? 'this vendo'} yet.
                        Add one above.
                     </Muted>
                  ) : filteredZones.length === 0 ? (
                     <Muted>No {zoneFilter} zones.</Muted>
                  ) : (
                     <ZoneTable
                        deviceId={selectedId}
                        platform={platform}
                        zones={pageZones}
                        onChanged={() => loadZones(selectedId)}
                     />
                  )}
               </ZoneTableCard>

               {pageCount > 1 && (
                  <Pager>
                     <PagerBtn disabled={safePage === 0} onClick={() => setPage((p) => p - 1)}>
                        ← Prev
                     </PagerBtn>
                     <PagerInfo>
                        Page {safePage + 1} of {pageCount}
                     </PagerInfo>
                     <PagerBtn
                        disabled={safePage >= pageCount - 1}
                        onClick={() => setPage((p) => p + 1)}
                     >
                        Next →
                     </PagerBtn>
                  </Pager>
               )}
            </>
         )}
      </Wrapper>
   );
};

export default WifiNetworks;


/* ---------------------------------------------------------------- *
 *  Zone table with inline label edit + inline-confirm delete.
 * ---------------------------------------------------------------- */
const ZoneTable = ({
   deviceId,
   platform,
   zones,
   onChanged,
}: {
   deviceId: string;
   platform: 'mikrotik' | 'openwrt';
   zones: VlanZone[];
   onChanged: () => void;
}) => {
   return (
      <Table>
         <thead>
            <tr>
               <Th>VLAN</Th>
               <Th>Status</Th>
               <Th>Label</Th>
               <Th>Subnet</Th>
               <Th>Gateway</Th>
               <Th>NodeMCU IP</Th>
               <Th>Sub-vendo</Th>
               <Th style={{ textAlign: 'right' }}>Actions</Th>
            </tr>
         </thead>
         <tbody>
            {zones.map((z) => (
               <ZoneRow
                  key={z.vlanId}
                  deviceId={deviceId}
                  platform={platform}
                  zone={z}
                  onChanged={onChanged}
               />
            ))}
         </tbody>
      </Table>
   );
};

const ZoneRow = ({
   deviceId,
   platform,
   zone,
   onChanged,
}: {
   deviceId: string;
   platform: 'mikrotik' | 'openwrt';
   zone: VlanZone;
   onChanged: () => void;
}) => {
   const [editing, setEditing] = useState(false);
   const [label, setLabel] = useState(zone.label);
   const [confirmDel, setConfirmDel] = useState(false);
   const [busy, setBusy] = useState(false);
   const attached = !!zone.attachedSub;

   const saveLabel = async () => {
      try {
         setBusy(true);
         await updateVlanZoneApi(deviceId, zone.vlanId, { label: label.trim() });
         setEditing(false);
         onChanged();
      } catch (e: any) {
         toast.error(e?.response?.data?.error || 'Failed to update label');
      } finally {
         setBusy(false);
      }
   };

   const doDelete = async () => {
      try {
         setBusy(true);
         const res = await deleteVlanZoneApi(deviceId, zone.vlanId);
         toast.success(res.msg);
         onChanged();
      } catch (e: any) {
         toast.error(e?.response?.data?.error || 'Failed to delete zone');
         setConfirmDel(false);
      } finally {
         setBusy(false);
      }
   };

   return (
      <tr>
         <Td>
            <VlanChip>{zone.vlanId}</VlanChip>
         </Td>
         <Td>
            <StatusCell
               title={
                  zone.status === 'pending' && platform === 'openwrt'
                     ? PENDING_HINT_OPENWRT
                     : STATUS_HINT[zone.status]
               }
            >
               <StatusDot $status={zone.status} />
               {STATUS_LABEL[zone.status]}
               {zone.status === 'failed' && zone.pushAttempts > 0 && (
                  <Muted> ({zone.pushAttempts} tries)</Muted>
               )}
            </StatusCell>
         </Td>
         <Td>
            {editing ? (
               <InlineEdit>
                  <Input
                     value={label}
                     autoFocus
                     placeholder="Label"
                     onChange={(e) => setLabel(e.target.value)}
                     onKeyDown={(e) => e.key === 'Enter' && saveLabel()}
                  />
                  <MiniBtn disabled={busy} onClick={saveLabel}>
                     Save
                  </MiniBtn>
                  <MiniBtn
                     $ghost
                     onClick={() => {
                        setEditing(false);
                        setLabel(zone.label);
                     }}
                  >
                     Cancel
                  </MiniBtn>
               </InlineEdit>
            ) : (
               <LabelCell onClick={() => setEditing(true)} title="Click to edit">
                  {zone.label || <Muted>—</Muted>}
               </LabelCell>
            )}
         </Td>
         <Td>
            <Mono>{zone.subnet}</Mono>
         </Td>
         <Td>
            <Mono>{zone.gateway}</Mono>
         </Td>
         <Td>
            <Mono>{zone.nodeMcuIp}</Mono>
         </Td>
         <Td>
            {attached ? (
               <SubBadge>{zone.attachedSub!.name}</SubBadge>
            ) : (
               // Not "Available": a VLAN with no sub-vendo sells nothing, and
               // that word told SoGood WiFi (2026-10-04) all was well while
               // every customer on it paid and got no internet.
               <AvailPill title="Customers on this VLAN cannot buy until you add a sub-vendo for it.">
                  No sub-vendo yet — not selling
               </AvailPill>
            )}
         </Td>
         <Td style={{ textAlign: 'right' }}>
            {confirmDel ? (
               <InlineConfirm>
                  <span>Delete VLAN {zone.vlanId}?</span>
                  <MiniBtn $danger disabled={busy} onClick={doDelete}>
                     {busy ? '…' : 'Yes'}
                  </MiniBtn>
                  <MiniBtn $ghost onClick={() => setConfirmDel(false)}>
                     No
                  </MiniBtn>
               </InlineConfirm>
            ) : (
               <DelBtn
                  disabled={attached}
                  title={
                     attached
                        ? 'Detach the sub-vendo first'
                        : `Delete VLAN ${zone.vlanId}`
                  }
                  onClick={() => setConfirmDel(true)}
               >
                  Delete
               </DelBtn>
            )}
         </Td>
      </tr>
   );
};

/* -------------------------------- styles -------------------------------- */
const Subtitle = styled.p`
   color: ${colors.GRAY1};
   font-size: 13px;
   margin: 4px 0 16px;
   max-width: 760px;
   line-height: 1.5;
`;

const SelectRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   margin-bottom: 14px;
   flex-wrap: wrap;
`;

const FieldLabel = styled.label`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY1};
`;

const Select = styled.select`
   padding: 8px 12px;
   border: 1px solid ${colors.PRIMARY}30;
   border-radius: 8px;
   font-size: 13px;
   background: ${colors.WHITE};
   min-width: 220px;
`;

const PlatformPill = styled.span`
   background: ${colors.INDIGO}20;
   color: ${colors.INDIGO};
   border-radius: 999px;
   padding: 3px 10px;
   font-size: 11px;
   font-weight: 700;
`;

const Spacer = styled.div`
   flex: 1;
`;

const AddBtn = styled.button`
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 8px;
   padding: 9px 16px;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
   &:hover:not(:disabled) {
      opacity: 0.9;
   }
`;

const RetryBtn = styled.button`
   background: ${colors.WHITE};
   color: ${colors.DANGER};
   border: 1px solid ${colors.DANGER};
   border-radius: 8px;
   padding: 9px 14px;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   margin-right: 8px;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
   &:hover:not(:disabled) {
      background: ${colors.DANGER};
      color: ${colors.WHITE};
   }
`;

const PushingNote = styled.div`
   margin-top: 8px;
   font-size: 12px;
   color: ${colors.PRIMARY};
   opacity: 0.85;
`;

// Used by the inline label edit in the zone table.
const Input = styled.input<{ $invalid?: boolean }>`
   padding: 9px 12px;
   border: 1px solid ${(p) => (p.$invalid ? colors.DANGER : `${colors.PRIMARY}30`)};
   border-radius: 8px;
   font-size: 13px;
   width: 100%;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const ZoneTableCard = styled.div`
   border: 1px solid ${colors.PRIMARY}20;
   border-radius: 12px;
   padding: 8px;
   background: ${colors.WHITE};
   overflow-x: auto;
`;

const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 13px;
`;

const Th = styled.th`
   text-align: left;
   padding: 10px 12px;
   font-size: 11px;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY1};
   border-bottom: 1px solid ${colors.PRIMARY}15;
   white-space: nowrap;
`;

const Td = styled.td`
   padding: 10px 12px;
   border-bottom: 1px solid ${colors.PRIMARY}10;
   vertical-align: middle;
`;

const VlanChip = styled.span`
   display: inline-block;
   background: ${colors.PRIMARY}15;
   color: ${colors.PRIMARY};
   border-radius: 6px;
   padding: 2px 9px;
   font-weight: 800;
   font-size: 12px;
`;

const Mono = styled.span`
   font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
   font-size: 12px;
   color: ${colors.MAIN_DARK};
`;

const Muted = styled.span`
   color: ${colors.GRAY1};
   font-size: 12px;
`;

const LabelCell = styled.div`
   cursor: pointer;
   min-height: 18px;
   &:hover {
      text-decoration: underline dotted;
   }
`;

const InlineEdit = styled.div`
   display: flex;
   gap: 6px;
   align-items: center;
   max-width: 280px;
`;

const InlineConfirm = styled.div`
   display: inline-flex;
   gap: 6px;
   align-items: center;
   font-size: 12px;
   color: ${colors.MAIN_DARK};
`;

const SubBadge = styled.span`
   background: ${colors.SUCCESS}20;
   color: ${colors.SUCCESS};
   border-radius: 999px;
   padding: 3px 10px;
   font-size: 11px;
   font-weight: 700;
`;

const AvailPill = styled.span`
   background: ${colors.ORANGE}12;
   color: ${colors.ORANGE};
   border: 1px solid ${colors.ORANGE}40;
   white-space: nowrap;
   border-radius: 999px;
   padding: 3px 10px;
   font-size: 11px;
   font-weight: 700;
`;

const FilterRow = styled.div`
   display: flex;
   gap: 8px;
   margin-bottom: 10px;
   flex-wrap: wrap;
`;

const FilterChip = styled.button<{ $active?: boolean }>`
   background: ${(p) => (p.$active ? colors.PRIMARY : 'transparent')};
   color: ${(p) => (p.$active ? colors.WHITE : colors.GRAY1)};
   border: 1px solid ${(p) => (p.$active ? colors.PRIMARY : `${colors.GRAY1}40`)};
   border-radius: 999px;
   padding: 6px 14px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:hover {
      border-color: ${colors.PRIMARY};
   }
`;

const Pager = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 14px;
   margin-top: 12px;
`;

const PagerBtn = styled.button`
   background: transparent;
   color: ${colors.PRIMARY};
   border: 1px solid ${colors.PRIMARY}40;
   border-radius: 8px;
   padding: 6px 14px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      color: ${colors.GRAY1};
      border-color: ${colors.GRAY1}40;
   }
`;

const PagerInfo = styled.span`
   font-size: 12px;
   color: ${colors.GRAY1};
   font-weight: 600;
`;

const MiniBtn = styled.button<{ $ghost?: boolean; $danger?: boolean }>`
   border: 1px solid
      ${(p) =>
         p.$danger ? colors.DANGER : p.$ghost ? `${colors.GRAY1}50` : colors.PRIMARY};
   background: ${(p) =>
      p.$ghost ? 'transparent' : p.$danger ? colors.DANGER : colors.PRIMARY};
   color: ${(p) => (p.$ghost ? colors.GRAY1 : colors.WHITE)};
   border-radius: 6px;
   padding: 5px 10px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

const DelBtn = styled.button`
   border: 1px solid ${colors.DANGER}50;
   background: transparent;
   color: ${colors.DANGER};
   border-radius: 6px;
   padding: 5px 12px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      border-color: ${colors.GRAY1}40;
      color: ${colors.GRAY1};
   }
`;

const EmptyCard = styled.div`
   border: 1px dashed ${colors.PRIMARY}40;
   border-radius: 12px;
   padding: 32px;
   text-align: center;
   color: ${colors.GRAY1};
   font-size: 14px;
`;

const ProgressPill = styled.span<{ $allLive?: boolean }>`
   background: ${(p) => (p.$allLive ? `${colors.SUCCESS}18` : `${colors.GRAY1}18`)};
   color: ${(p) => (p.$allLive ? colors.SUCCESS : colors.MAIN_DARK)};
   border-radius: 999px;
   padding: 4px 12px;
   font-size: 12px;
   font-weight: 800;
   white-space: nowrap;
`;

const Dim = styled.span`
   color: ${colors.GRAY1};
   font-weight: 700;
`;

const Fail = styled.span`
   color: ${colors.DANGER};
   font-weight: 700;
`;

const StatusCell = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 7px;
   font-size: 12px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   white-space: nowrap;
`;

const StatusDot = styled.span<{ $status: VlanZone['status'] }>`
   width: 9px;
   height: 9px;
   border-radius: 50%;
   flex-shrink: 0;
   background: ${(p) =>
      p.$status === 'live'
         ? colors.SUCCESS
         : p.$status === 'failed'
         ? colors.DANGER
         : colors.GRAY1};
   ${(p) =>
      p.$status === 'pending' &&
      `animation: sfPulse 1.2s ease-in-out infinite;`}
   @keyframes sfPulse {
      0%,
      100% {
         opacity: 0.35;
      }
      50% {
         opacity: 1;
      }
   }
`;
